<script setup>
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { i18n } from 'boot/i18n';
import { CapacitorFirebaseMFA } from 'capacitor-firebase-mfa';
import { useDialogPluginComponent } from 'quasar';
import { HrAdaptiveFormDialog, HrConfirmBtn, HrDismissBtn, notifyNegative } from 'src/design-system';
import AppUtils from 'src/utils/AppUtils';
import { logEvent } from 'src/utils/LogEvent';
import { ref } from 'vue';

/** @typedef {import('@capacitor-community/apple-sign-in').SignInWithAppleOptions} SignInWithAppleOptions */

defineOptions({ name: 'AppleReauthenticate' });

const props = defineProps({
  appleAuthData: {
    type: Object,
    required: true,
  },
});

defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

const appleAuthSpinner = ref(false);

async function nativeAppleAuthorize() {
  const { nonce } = await CapacitorFirebaseMFA.createRandomNonce();
  /** @type {SignInWithAppleOptions} */
  const options = {
    ...props.appleAuthData,
    nonce: await AppUtils.hashString(nonce),
  };

  const { response } = await SignInWithApple.authorize(options);
  return { response, rawNonce: nonce };
}

async function onAppleReauthenticate() {
  appleAuthSpinner.value = true;
  try {
    const { response, rawNonce } = await nativeAppleAuthorize();
    const { value } = await CapacitorFirebaseMFA.loginWithAppleAndSendMfaCode({
      idToken: response.identityToken,
      nonce: rawNonce,
    });

    onDialogOK(value);
  } catch (err) {
    console.error(err);
    logEvent(err);
    notifyNegative(err.message);
  } finally {
    appleAuthSpinner.value = false;
  }
}
</script>

<template>
  <hr-adaptive-form-dialog
    ref="dialogRef"
    :title="i18n.t('users.modals.reAuthenticate.title')"
    @hide="onDialogHide"
    @close="onDialogCancel"
  >
    <p>{{ i18n.t('users.modals.appleReauthenticate.textCaption') }}</p>
    <template #actions>
      <hr-dismiss-btn
        :label="i18n.t('common.buttonCancel')"
        @click="onDialogCancel"
      />
      <hr-confirm-btn
        icon="fab fa-apple apple-login-icon"
        :label="i18n.t('users.modals.reAuthenticate.buttonReAuthenticateWithApple')"
        :loading="appleAuthSpinner"
        @click="onAppleReauthenticate"
      />
    </template>
  </hr-adaptive-form-dialog>
</template>
